/* eslint-disable no-var */
/* eslint-disable no-unused-vars */
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import promise from 'redux-promise';
import { configureStore } from '@reduxjs/toolkit';
import { Router, Route } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import moment from 'moment-timezone';
import { createEpicMiddleware } from 'redux-observable';

import { SENTRY_DSN, SENTRY_ENV, SENTRY_RELEASE } from './config';
import Root from './components/Root';
import reducers from './reducers';
import './components/bundle.scss';
import { fetchLoggedInAsync } from './actions/ClientData';
import epics from './epics/combinedEpics';
import * as api from './epics/api';
import history from './history';
import { constructionTypes, hospitalityTypes, industryTypes, officeTypes, vehicleTypes } from './consts';

moment.locale('en-gb');
moment.tz.setDefault('Europe/London');

if (SENTRY_DSN) {
    Sentry.init({
        dsn: SENTRY_DSN,
        environment: SENTRY_ENV,
        release: SENTRY_RELEASE,
        sendDefaultPii: true,
        integrations: [],
    });
}
axios.defaults.withCredentials = true;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const defaultState: any = {
    // move default state to be integrated as part of the app when  user arrives at that point
    requestData: {
        bookingRequest: {
            service: null,
            date: null,
            dates: [],
            rangeDatesSelected: [],
            isMultiple: false,
            isRemoving: false,
            start_time: null,
            duration: 4.0,
            recurrence_period: 0,
            number_of_freelancers: 1,
            address1: null,
            address2: null,
            city: null,
            postcode: null,
            admin_information: '',
            freelancers_information: '',
            nature_of_business: '',
            required_qualifications: '',
            safety_risks: '',
            available_facilities: '',
            expenses_payable: '',
            title: '',
            default_nature_of_business: '',
            default_required_qualifications: '',
            default_safety_risks: '',
            default_available_facilities:
                'Site specific facilities will be confirmed at client induction',
            default_expenses_payable: 'None',
            dynamic_opt_in: 0,
            voucher: null,
            matching_mode: 'MM',
            dbs_check: 'NO',
        },
        roleData: {
            construction: {
                role: constructionTypes.GENERAL,
                adt: 0,
                aluminium_steel: 0,
                blockwork_facework: 0,
                brickwork: 0,
                builder: 0,
                carpentry: 0,
                cis_rs: 0,
                civils_operative: 0,
                coded: 0,
                commercial_industrial: 0,
                cpcs_npors: 0,
                cscs: 0,
                domestic: 0,
                dozer: 0,
                drainage_deep_drainage: 0,
                drawings_literate: 0,
                dry_liner: 0,
                edition_18: 0,
                electrical_mate: 0,
                estimator: 0,
                excavator_180: 0,
                excavator_360: 0,
                external: 0,
                fabrication: 0,
                first_aid: 0,
                fix_1: 0,
                fix_2: 0,
                foreman: 0,
                forward_tip_dumper: 0,
                ganger: 0,
                gas_safe: 0,
                gold_black_cscs: 0,
                groundworks: 0,
                hand_tools: 0,
                handballing: 0,
                housebashing: 0,
                housebashing_line_work: 0,
                internal: 0,
                joiner: 0,
                maintenance: 0,
                mason: 0,
                mewp_ipaf: 0,
                mig_tig_stick: 0,
                non_cscs: 0,
                painting: 0,
                painting_decorating: 0,
                paper_hanging: 0,
                part_1: 0,
                part_2: 0,
                patchwork: 0,
                plastering: 0,
                plumbers_mate: 0,
                ppe: 0,
                rams: 0,
                refurb: 0,
                renderer: 0,
                repointing: 0,
                ride_on_roller: 0,
                roofing: 0,
                shopfitting: 0,
                shuttering_formwork: 0,
                site_supervisor: 0,
                skilled_labourer: 0,
                skimming: 0,
                slabs_edges_kerbs: 0,
                smsts: 0,
                spraying: 0,
                stone_mason: 0,
                street_works_operative: 0,
                tape_jointing: 0,
                telehandler: 0,
                testing: 0,
                woodwork: 0,
            },
            driver: {
                vehicle_type: vehicleTypes.CAR,
                medium_van: 0,
                large_van: 0,
                minimum_delivery_box: 0,
                own_vehicle: 1,
                adr: 0,
                hiab: 0,
            },
            hospitality: {
                role: hospitalityTypes.WAITER,
                supervisor: 0,
                cocktail_making: 0,
                heavy_lifting: 0,
                food_preparation: 0,
                sous_chef: 0,
                commis_chef: 0,
                chef_de_partie: 0,
                pastry_chef: 0,
            },
            industry: {
                role: industryTypes.GENERAL,
                forklift_reach: 0,
                forklift_counterbalance: 0,
            },
            office: {
                role: officeTypes.OFFICE_GENERAL,
                legal_secretary: 0,
                solicitors: 0,
                legal_administrator: 0,
                purchasing_manager: 0,
                planner: 0,
                buyer: 0,
                data_entry: 0,
                assistant: 0,
                sales_administrator: 0,
                operations_assistant: 0,
                head_receptionist: 0,
                inbound_sales: 0,
                outbound_sales: 0,
                business_development_manager: 0,
                field_sales_manager: 0,
                telesales_representative: 0,
                sales_executive: 0,
                debit_collector: 0,
                call_centre: 0,
                account_manager: 0,
                customer_service_manager: 0,
                scheduler: 0,
                sales_support: 0,
                banking: 0,
                retail_assistant: 0,
                manager: 0,
                recruitment_resourcer: 0,
                recruitment_manager: 0,
                temporaries_consultant: 0,
                permanent_consultant: 0,
                projects: 0,
                it_manager: 0,
                helpdesk_1st_line: 0,
                helpdesk_2nd_line: 0,
                helpdesk_3rd_line: 0,
                web_designer: 0,
                software_developer: 0,
                it_director: 0,
                accounts_assistant: 0,
                accountant: 0,
                purchase_ledger: 0,
                sales_ledger: 0,
                payroll: 0,
                bookkeeper: 0,
                finance_manager: 0,
                quality_assurance: 0,
                audits: 0,
                marketing_assistant: 0,
                marketing_manager: 0,
                marketing_executive: 0,
                digital_marketing: 0,
                product_manager: 0,
                hr_administration: 0,
                hr_manager: 0,
                hr_executive: 0,
                hr_consultant: 0,
                office_manager: 0,
                managing_director: 0,
                operation_manager: 0,
                junior: 0,
                mid_weight: 0,
                regulatory_graphic_designer: 0,
                junior_project_manager: 0,
                project_coordinator: 0,
            }
        },
        paygrade: {},
        jobRequest: {
            id: null,
        },
    },
    clientData: {
        error: {
            postcode: null,
            confirmJob: null,
        },
        basicInfo: {},
        buzzworkers: null,
        customers: null,
        loggedIn: false,
        signedUp: false,
        clientAddressList: [],
    },
    view: {
        rolePopout: {
            roleExtras: false,
            shiftTimes: false,
        },
        modals: {
            authModal: null,
            paymentModal: null,
        },
        successMessage: null,
    },
    loading: {
        postcode: false,
        paygrade: false,
        postPayment: false,
    },
};

const middleware = [
    promise,
    createEpicMiddleware(epics, { dependencies: { api } }),
];

const enhancers = [
    ...(SENTRY_DSN ? [Sentry.createReduxEnhancer()] : []),
];

const store = configureStore({
    reducer: reducers,
    preloadedState: defaultState,
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [
                    'LOAD_BOOKINGS',
                    'LOAD_CLIENTS',
                    'LOAD_FREELANCERS',
                    'LOAD_SHIFTS',
                    'LOAD_TRACKING',
                    'LOAD_UNALLOCATED_FREELANCERS',
                ],
            }
        }).concat(...middleware),
    enhancers: getDefaultEnhancers =>
        getDefaultEnhancers().concat(...enhancers),
    devTools: process.env.CURRENT_ENV === 'local',
});

ReactDOM.render(
    <div className="initial-loading">
        <i className="icon icon-loading-1" />
    </div>,
    document.getElementById('react-root'),
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const completion: any = (response: any, error: any) => {
    // const loggedIn = (response && !error);
    // if (!loggedIn) {
    //     store.dispatch(toggleModal('authModal', 'signup'));
    // }
    //= ==================================================
    const render = () => {
        ReactDOM.render(
            <Provider store={store}>
                <Router history={history}>
                    <Route path="/" component={Root} />
                </Router>
            </Provider>,
            document.getElementById('react-root'),
        );
    };

    render();
    //= ==================================================
};
store.dispatch<any>(fetchLoggedInAsync(completion));

document.addEventListener('scroll', () => {
    const header = document.querySelector('header.hidden-xs');
    if (header) {
        if (window.scrollY > 50) {
            header.classList.add('active');
        } else {
            header.classList.remove('active');
        }
    }
});
